import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { Tab, Tabs, Table, Spinner, Badge, Form, Button, Alert, Container, Card } from "react-bootstrap";
import {
  MdUpload,
  MdDownload,
  MdCloudUpload,
  MdLogin,
  MdCheckCircle,
  MdError,
  MdCalendarToday
} from "react-icons/md";
import checkInManualApi from "../../../services/api/checkInManualApi";
import * as XLSX from "xlsx";
import "react-toastify/dist/ReactToastify.css";

const CheckInManual = () => {
  const currentUser = JSON.parse(localStorage.getItem("user"));
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject"));

  // Estados para check-in masivo
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [fileValidationErrors, setFileValidationErrors] = useState([]);
  const [autoCompleteDniLength, setAutoCompleteDniLength] = useState(false);
  const [skipDuplicateValidation, setSkipDuplicateValidation] = useState(false);
  
  // Estados para check-in individual
  const [activeTab, setActiveTab] = useState("masivo");
  const [dni, setDni] = useState("");
  const [fechaCheckIn, setFechaCheckIn] = useState("");
  const [zona, setZona] = useState("");
  const [modulo, setModulo] = useState("");
  const [habitacion, setHabitacion] = useState("");
  const [cama, setCama] = useState("");
  const [realizandoCheckIn, setRealizandoCheckIn] = useState(false);

  const showToast = (type, message, options = {}) => {
    if (type === "success") {
      toast.success(message, { position: "bottom-right", autoClose: 3000, ...options });
    } else if (type === "error") {
      toast.error(message, { position: "bottom-right", autoClose: 3000, ...options });
    } else if (type === "info") {
      toast.info(message, { position: "bottom-right", autoClose: 3000, ...options });
    } else if (type === "warning") {
      toast.warning(message, { position: "bottom-right", autoClose: 3000, ...options });
    }
  };

  // ========== CHECK-IN MASIVO ==========
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileValidationErrors([]);
      setResults(null);
    }
  };

  const downloadTemplate = () => {
    const template = [
      {
        "Fecha Check-in": "11-03-2026",
        "Numero Documento Identidad": "12345678",
        "Zona": "Zona A",
        "Modulo": "Módulo 1",
        "Habitacion": "101",
        "Cama": "1"
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Check-In Manual");
    XLSX.writeFile(wb, "plantilla_checkin_manual.xlsx");
    showToast("info", "Plantilla descargada");
  };

  const processCheckIns = async () => {
    if (!file) {
      showToast("error", "Por favor seleccione un archivo");
      return;
    }

    setIsLoading(true);
    setResults(null);
    setFileValidationErrors([]);

    try {
      // Paso 1: Validar el archivo primero
      showToast("info", "Validando archivo...");
      const validationResponse = await checkInManualApi.validateFile(
        file,
        selectedProject?.id,
        autoCompleteDniLength,
        skipDuplicateValidation
      );

      if (!validationResponse.data?.success) {
        showToast("error", validationResponse.data?.message || "Error al validar el archivo");
        setIsLoading(false);
        return;
      }

      const { total_filas, total_errores, errores } = validationResponse.data;
      
      // Si hay errores de validación, mostrarlos y detener el proceso
      if (total_errores > 0) {
        setFileValidationErrors(errores || []);
        showToast(
          "error",
          `❌ El archivo contiene ${total_errores} error(es). DEBE corregir todos los errores antes de continuar.`
        );
        setIsLoading(false);
        return;
      }

      // Si la validación fue exitosa, continuar con el procesamiento
      showToast("success", `✅ Validación exitosa. Procesando ${total_filas} check-ins...`);

      // Paso 2: Procesar los check-ins
      const response = await checkInManualApi.procesarCheckIns(
        file,
        selectedProject?.id,
        autoCompleteDniLength,
        skipDuplicateValidation
      );

      // Manejar respuesta del procesamiento
      if (response.data?.success) {
        setResults(response.data);
        const { checkins_exitosos, checkins_fallidos } = response.data;
        
        if (checkins_fallidos === 0) {
          showToast("success", `✅ Todos los check-ins se procesaron exitosamente (${checkins_exitosos} exitosos)`);
        } else if (checkins_exitosos > 0) {
          showToast(
            "warning",
            `⚠️ Check-in completado con algunos problemas: ${checkins_exitosos} exitosos, ${checkins_fallidos} fallidos`
          );
        } else {
          showToast("error", `❌ Todos los check-ins fallaron (${checkins_fallidos} fallidos)`);
        }
      } else {
        showToast("error", response.data?.message || "Error al procesar check-ins");
      }
    } catch (error) {
      console.error("Error al procesar check-ins:", error);
      showToast(
        "error",
        error.response?.data?.detail || error.message || "Error al procesar el archivo"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setResults(null);
    setFileValidationErrors([]);
    setAutoCompleteDniLength(false);
    setSkipDuplicateValidation(false);
    // Reset input file
    const fileInput = document.getElementById("fileInput");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  // ========== CHECK-IN INDIVIDUAL ==========
  const handleCheckInIndividual = async (e) => {
    e.preventDefault();
    
    if (!dni || !fechaCheckIn || !zona || !modulo || !habitacion || !cama) {
      showToast("error", "Por favor complete todos los campos");
      return;
    }

    setRealizandoCheckIn(true);

    try {
      const response = await checkInManualApi.realizarCheckInIndividual(
        dni,
        fechaCheckIn,
        zona,
        modulo,
        habitacion,
        cama,
        selectedProject?.id,
        autoCompleteDniLength
      );

      if (response.data?.success) {
        showToast("success", response.data.message || "Check-in realizado exitosamente");
        resetIndividualForm();
      } else {
        showToast("error", response.data?.message || "Error al realizar check-in");
      }
    } catch (error) {
      console.error("Error al realizar check-in individual:", error);
      showToast(
        "error",
        error.response?.data?.detail || error.message || "Error al realizar check-in"
      );
    } finally {
      setRealizandoCheckIn(false);
    }
  };

  const resetIndividualForm = () => {
    setDni("");
    setFechaCheckIn("");
    setZona("");
    setModulo("");
    setHabitacion("");
    setCama("");
  };

  // Descargar detalles de check-ins fallidos
  const downloadFailedCheckIns = () => {
    if (!results?.detalles_fallidos?.length) {
      showToast("warning", "No hay check-ins fallidos para descargar");
      return;
    }

    const excelData = results.detalles_fallidos.map((item, index) => ({
      'No.': index + 1,
      'Fila Excel': item.fila,
      'DNI': item.dni,
      'Error': item.error
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    ws['!cols'] = [
      { wch: 6 },
      { wch: 12 },
      { wch: 15 },
      { wch: 60 }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Check-ins Fallidos");

    const fecha = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `checkins_fallidos_${fecha}.xlsx`);

    showToast("success", `Excel descargado: ${results.detalles_fallidos.length} check-ins fallidos`);
  };

  return (
    <>
      {/* Digital Harmony Styles para Check-In Manual */}
      <style>
        {`
          /* Fondo y contenedor principal */
          .dh-checkin-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
            padding: 2rem 0;
          }
          
          /* Card principal con Digital Harmony */
          .dh-card-main {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
            margin-bottom: 2rem;
          }
          
          /* Header del card */
          .dh-card-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-radius: 20px 20px 0 0;
            padding: 1.5rem 2rem;
            border: none;
          }
          
          .dh-header-title {
            color: white;
            font-weight: 700;
            font-size: 1.5rem;
            margin: 0;
            display: flex;
            align-items: center;
          }
          
          /* Botones con Digital Harmony */
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          
          .btn-dh-primary:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }
          
          .btn-dh-warning {
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(240, 147, 251, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-warning:hover {
            background: linear-gradient(135deg, #f5576c 0%, #e91e63 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(240, 147, 251, 0.4);
            color: white;
          }
          
          .btn-dh-info {
            background: linear-gradient(135deg, #00c6ff 0%, #0072ff 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(0, 198, 255, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-info:hover {
            background: linear-gradient(135deg, #0072ff 0%, #0056cc 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 198, 255, 0.4);
            color: white;
          }
          
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }

          .btn-dh-danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(239, 68, 68, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-danger:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
            color: white;
          }
          
          /* Alert mejorado */
          .dh-alert-info {
            background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
            border: 2px solid #3b82f6;
            border-radius: 15px;
            padding: 1.5rem;
            color: #1e40af;
            margin: 1.5rem 0;
          }
          
          .dh-alert-danger {
            background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
            border: 2px solid #ef4444;
            border-radius: 15px;
            padding: 1.5rem;
            color: #991b1b;
            margin: 1.5rem 0;
          }

          .dh-alert-success {
            background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
            border: 2px solid #10b981;
            border-radius: 15px;
            padding: 1.5rem;
            color: #065f46;
            margin: 1.5rem 0;
          }
          
          /* File upload section */
          .dh-upload-section {
            background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
            border: 2px dashed #16a34a;
            border-radius: 15px;
            padding: 2rem;
            text-align: center;
            transition: all 0.3s ease;
            margin: 1.5rem 0;
            cursor: pointer;
          }
          
          .dh-upload-section:hover {
            border-color: #15803d;
            background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
            transform: translateY(-2px);
          }
          
          .dh-upload-section.has-file {
            border-color: #059669;
            background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
            box-shadow: 0 8px 25px rgba(16, 185, 129, 0.1);
          }
          
          .upload-icon {
            color: #16a34a;
            font-size: 3rem;
            margin-bottom: 1rem;
          }
          
          .upload-text {
            color: #374151;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 0.5rem;
          }
          
          .upload-subtext {
            color: #6b7280;
            font-size: 0.9rem;
          }
          
          /* Checkboxes mejorados */
          .dh-checkbox-container {
            background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
            border: 2px solid #f59e0b;
            border-radius: 12px;
            padding: 1.25rem;
            margin-bottom: 1rem;
            transition: all 0.3s ease;
          }
          
          .dh-checkbox-container:hover {
            border-color: #d97706;
            box-shadow: 0 4px 12px rgba(245, 158, 11, 0.2);
          }
          
          .dh-checkbox-label {
            font-weight: 600;
            color: #78350f;
            margin-bottom: 0.25rem;
          }
          
          .dh-checkbox-description {
            color: #92400e;
            font-size: 0.875rem;
          }
          
          /* Form controls */
          .form-control-dh {
            border: 2px solid #e5e7eb;
            border-radius: 12px;
            padding: 12px 16px;
            font-size: 1rem;
            transition: all 0.3s ease;
            background: white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.02);
          }
          
          .form-control-dh:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.25rem rgba(102, 126, 234, 0.15);
            background: white;
          }
          
          .form-label-dh {
            font-weight: 700;
            color: #374151;
            margin-bottom: 0.75rem;
            font-size: 0.95rem;
          }
          
          /* Tabs mejorados */
          .nav-tabs .nav-link {
            border-radius: 12px 12px 0 0;
            font-weight: 600;
            padding: 1rem 2rem;
            transition: all 0.3s ease;
          }
          
          .nav-tabs .nav-link.active {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
          }
          
          /* Badges */
          .badge-dh-success {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 8px;
            font-size: 1rem;
            font-weight: 600;
          }
          
          .badge-dh-danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 8px;
            font-size: 1rem;
            font-weight: 600;
          }
          
          /* Table styles */
          .table-dh {
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          }
          
          .table-dh thead {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          
          .table-dh tbody tr:hover {
            background-color: #f3f4f6;
          }
        `}
      </style>

      <div className="dh-checkin-bg">
        <Container fluid>
          <ToastContainer />
          
          <Card className="dh-card-main">
            <Card.Header className="dh-card-header">
              <h2 className="dh-header-title">
                <MdLogin style={{ marginRight: "15px", fontSize: "2rem" }} />
                Check-In Manual de Trabajadores
              </h2>
            </Card.Header>

            <Card.Body className="p-4">
              {!currentUser || !selectedProject ? (
                <Alert variant="warning" className="dh-alert-info">
                  Por favor inicia sesión y selecciona un proyecto
                </Alert>
              ) : (
                <Tabs
                  id="checkin-tabs"
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k)}
                  className="mb-4"
                >
                  {/* ========== TAB: CHECK-IN MASIVO ========== */}
                  <Tab eventKey="masivo" title="Check-In Masivo">
                    <div className="dh-alert-info">
                      <strong>📋 Instrucciones:</strong>
                      <ul className="mb-0 mt-2">
                        <li>Descargue la plantilla Excel y complete los datos requeridos</li>
                        <li>Columnas requeridas: <strong>Fecha Check-in, Numero Documento Identidad, Zona, Modulo, Habitacion, Cama</strong></li>
                        <li>Formato de fecha: <strong>DD-MM-YYYY</strong> (ejemplo: 11-03-2026)</li>
                        <li>El sistema validará que los trabajadores existan y las camas estén disponibles</li>
                        <li>Solo se permite check-in en camas libres o reservadas</li>
                      </ul>
                    </div>

                    {/* Opciones */}
                    <div className="row mb-4">
                      <div className="col-md-6 mb-3">
                        <div className="dh-checkbox-container">
                          <Form.Check
                            type="checkbox"
                            id="autoCompleteDniLength"
                            checked={autoCompleteDniLength}
                            onChange={(e) => setAutoCompleteDniLength(e.target.checked)}
                            label={
                              <div>
                                <div className="dh-checkbox-label">✓ Autocompletar longitud de DNI</div>
                                <div className="dh-checkbox-description">
                                  Completa automáticamente el número de documento con ceros a la izquierda según la longitud configurada
                                </div>
                              </div>
                            }
                          />
                        </div>
                      </div>

                      <div className="col-md-6 mb-3">
                        <div className="dh-checkbox-container">
                          <Form.Check
                            type="checkbox"
                            id="skipDuplicateValidation"
                            checked={skipDuplicateValidation}
                            onChange={(e) => setSkipDuplicateValidation(e.target.checked)}
                            label={
                              <div>
                                <div className="dh-checkbox-label">✓ Permitir check-in con asignación activa</div>
                                <div className="dh-checkbox-description">
                                  Permite realizar check-in aunque el trabajador ya tenga una cama asignada
                                </div>
                              </div>
                            }
                          />
                        </div>
                      </div>
                    </div>

                    {/* Botones de plantilla y carga */}
                    <div className="row mb-4">
                      <div className="col-md-6">
                        <Button
                          variant="outline-primary"
                          onClick={downloadTemplate}
                          className="btn-dh-info w-100 py-3"
                        >
                          <MdDownload size={24} className="me-2" />
                          Descargar Plantilla Excel
                        </Button>
                      </div>
                      <div className="col-md-6">
                        <label htmlFor="fileInput" className={`dh-upload-section mb-0 ${file ? 'has-file' : ''}`}>
                          <div className="upload-icon">
                            {file ? <MdCheckCircle /> : <MdUpload />}
                          </div>
                          <div className="upload-text">
                            {file ? file.name : "Haz clic para seleccionar archivo"}
                          </div>
                          <div className="upload-subtext">
                            {file ? "Archivo listo para procesar" : "Formato: .xlsx, .xls"}
                          </div>
                        </label>
                        <input
                          type="file"
                          id="fileInput"
                          style={{ display: "none" }}
                          accept=".xlsx,.xls"
                          onChange={handleFileChange}
                        />
                      </div>
                    </div>

                    {/* Botones de acción */}
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <Button
                          onClick={processCheckIns}
                          disabled={!file || isLoading}
                          className="btn-dh-primary w-100 py-3"
                        >
                          {isLoading ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                              />
                              Procesando...
                            </>
                          ) : (
                            <>
                              <MdCloudUpload size={24} className="me-2" />
                              Procesar Check-Ins
                            </>
                          )}
                        </Button>
                      </div>
                      <div className="col-md-6">
                        <Button
                          onClick={resetForm}
                          disabled={isLoading}
                          className="btn-dh-secondary w-100 py-3"
                        >
                          Limpiar Formulario
                        </Button>
                      </div>
                    </div>

                    {/* Errores de validación */}
                    {fileValidationErrors.length > 0 && (
                      <div className="dh-alert-danger">
                        <h5>
                          <MdError size={24} className="me-2" />
                          Errores de Validación ({fileValidationErrors.length})
                        </h5>
                        <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                          <Table striped bordered hover size="sm" className="table-dh mt-3">
                            <thead>
                              <tr>
                                <th style={{ width: "10%" }}>Fila</th>
                                <th>Errores</th>
                              </tr>
                            </thead>
                            <tbody>
                              {fileValidationErrors.map((error, index) => (
                                <tr key={index}>
                                  <td>{error.fila}</td>
                                  <td>
                                    <ul className="mb-0">
                                      {error.errores.map((err, idx) => (
                                        <li key={idx}>{err}</li>
                                      ))}
                                    </ul>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    )}

                    {/* Resultados del procesamiento */}
                    {results && (
                      <div className="dh-alert-success">
                        <h5>
                          <MdCheckCircle size={24} className="me-2" />
                          Resultados del Procesamiento
                        </h5>
                        <div className="row mt-3">
                          <div className="col-md-4">
                            <div className="badge-dh-success w-100 mb-2">
                              ✓ Exitosos: {results.checkins_exitosos}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="badge-dh-danger w-100 mb-2">
                              ✗ Fallidos: {results.checkins_fallidos}
                            </div>
                          </div>
                          <div className="col-md-4">
                            {results.checkins_fallidos > 0 && (
                              <Button
                                variant="outline-danger"
                                onClick={downloadFailedCheckIns}
                                className="w-100"
                              >
                                <MdDownload className="me-1" />
                                Descargar Errores
                              </Button>
                            )}
                          </div>
                        </div>

                        {results.detalles_exitosos && results.detalles_exitosos.length > 0 && (
                          <div className="mt-4">
                            <h6>✅ Check-ins Exitosos ({results.detalles_exitosos.length}):</h6>
                            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                              <Table striped bordered hover size="sm" className="table-dh mt-2">
                                <thead>
                                  <tr>
                                    <th>Fila</th>
                                    <th>DNI</th>
                                    <th>Trabajador</th>
                                    <th>Cama</th>
                                    <th>Fecha Check-in</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {results.detalles_exitosos.map((detalle, index) => (
                                    <tr key={index}>
                                      <td>{detalle.fila}</td>
                                      <td>{detalle.dni}</td>
                                      <td>{detalle.trabajador}</td>
                                      <td>{detalle.cama}</td>
                                      <td>{detalle.fecha_checkin}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </div>
                        )}

                        {results.detalles_fallidos && results.detalles_fallidos.length > 0 && (
                          <div className="mt-4">
                            <h6>❌ Check-ins Fallidos ({results.detalles_fallidos.length}):</h6>
                            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                              <Table striped bordered hover size="sm" className="table-dh mt-2">
                                <thead>
                                  <tr>
                                    <th style={{ width: "10%" }}>Fila</th>
                                    <th style={{ width: "15%" }}>DNI</th>
                                    <th>Error</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {results.detalles_fallidos.map((detalle, index) => (
                                    <tr key={index}>
                                      <td>{detalle.fila}</td>
                                      <td>{detalle.dni}</td>
                                      <td className="text-danger">{detalle.error}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Tab>

                  {/* ========== TAB: CHECK-IN INDIVIDUAL ========== */}
                  <Tab eventKey="individual" title="Check-In Individual">
                    <div className="dh-alert-info mb-4">
                      <strong>🎯 Check-In Individual:</strong> Complete el formulario para realizar check-in de un trabajador en una cama específica
                    </div>

                    {/* Opción de autocompletar */}
                    <div className="dh-checkbox-container mb-4">
                      <Form.Check
                        type="checkbox"
                        id="autoCompleteDniLengthIndividual"
                        checked={autoCompleteDniLength}
                        onChange={(e) => setAutoCompleteDniLength(e.target.checked)}
                        label={
                          <div>
                            <div className="dh-checkbox-label">✓ Autocompletar longitud de DNI</div>
                            <div className="dh-checkbox-description">
                              Completa automáticamente el número de documento con ceros a la izquierda
                            </div>
                          </div>
                        }
                      />
                    </div>

                    <Form onSubmit={handleCheckInIndividual}>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <Form.Group>
                            <Form.Label className="form-label-dh">DNI / Documento de Identidad *</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Ingrese DNI"
                              value={dni}
                              onChange={(e) => setDni(e.target.value)}
                              required
                              className="form-control-dh"
                            />
                          </Form.Group>
                        </div>
                        <div className="col-md-6 mb-3">
                          <Form.Group>
                            <Form.Label className="form-label-dh">
                              <MdCalendarToday className="me-2" />
                              Fecha Check-In *
                            </Form.Label>
                            <Form.Control
                              type="date"
                              value={fechaCheckIn}
                              onChange={(e) => setFechaCheckIn(e.target.value)}
                              required
                              className="form-control-dh"
                            />
                          </Form.Group>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <Form.Group>
                            <Form.Label className="form-label-dh">Zona *</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Ejemplo: Zona A"
                              value={zona}
                              onChange={(e) => setZona(e.target.value)}
                              required
                              className="form-control-dh"
                            />
                          </Form.Group>
                        </div>
                        <div className="col-md-6 mb-3">
                          <Form.Group>
                            <Form.Label className="form-label-dh">Módulo *</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Ejemplo: Módulo 1"
                              value={modulo}
                              onChange={(e) => setModulo(e.target.value)}
                              required
                              className="form-control-dh"
                            />
                          </Form.Group>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <Form.Group>
                            <Form.Label className="form-label-dh">Habitación *</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Ejemplo: 101"
                              value={habitacion}
                              onChange={(e) => setHabitacion(e.target.value)}
                              required
                              className="form-control-dh"
                            />
                          </Form.Group>
                        </div>
                        <div className="col-md-6 mb-3">
                          <Form.Group>
                            <Form.Label className="form-label-dh">Cama *</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Ejemplo: 1"
                              value={cama}
                              onChange={(e) => setCama(e.target.value)}
                              required
                              className="form-control-dh"
                            />
                          </Form.Group>
                        </div>
                      </div>

                      <div className="row mt-4">
                        <div className="col-md-6">
                          <Button
                            type="submit"
                            disabled={realizandoCheckIn}
                            className="btn-dh-primary w-100 py-3"
                          >
                            {realizandoCheckIn ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  className="me-2"
                                />
                                Realizando Check-In...
                              </>
                            ) : (
                              <>
                                <MdLogin size={24} className="me-2" />
                                Realizar Check-In
                              </>
                            )}
                          </Button>
                        </div>
                        <div className="col-md-6">
                          <Button
                            onClick={resetIndividualForm}
                            disabled={realizandoCheckIn}
                            className="btn-dh-secondary w-100 py-3"
                          >
                            Limpiar Formulario
                          </Button>
                        </div>
                      </div>
                    </Form>
                  </Tab>
                </Tabs>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
    </>
  );
};

export default CheckInManual;
